/**
 * Подписи площадей комнат. Отдельный слой, а не часть RoomsLayer2D: подписи
 * должны лежать поверх мебели и стен, а заливка комнат — под ними.
 * Каждая подпись уходит от люстры и мебели в ближайшее свободное место у центра.
 * На плане потолков (слой потолков включён) к площади добавляется чистовая
 * высота комнаты с подвесным потолком — как отметка на рабочем чертеже.
 * При отдалении подпись упрощается до названия, а в совсем мелкой комнате уходит
 * (`roomLabelDetail`): иначе на обзорном масштабе подписи налезают друг на друга.
 */
import {
	RoomLabel,
	roomFinishedCeilingHeight,
	roomLabelCeilingHeight,
	roomDisplayName,
	roomLabelText,
	roomLabelDetail,
	type Room,
	type RoomLabelDetail,
} from "../../plan";
import { useEditorStore } from "@/widgets/editor";
import type { PointerEvent } from "react";
import { useProjectStore } from "@/shared/store";

/** Уровни подписи от самого подробного; номер — чтобы брать наименьший из двух */
const DETAILS: RoomLabelDetail[] = ["none", "name", "full"];
const DETAIL_RANK: Record<RoomLabelDetail, number> = { none: 0, name: 1, full: 2 };

interface Props {
	rooms: Room[];
	mmPerPx: number;
	/** Нажали на подпись комнаты — холст откроет переименование; без него подписи не кликаются */
	onLabelPointerDown?: (e: PointerEvent<SVGGElement>, room: Room) => void;
}

export const RoomLabels2D = ({ rooms, mmPerPx, onLabelPointerDown }: Props) => {
	const layers = useProjectStore((s) => s.project.layers);
	const showAreas = useEditorStore((s) => s.showAreas);
	if (!layers.rooms.visible || !showAreas) return null;

	// Уровень подписи у всех комнат один — по типичной (средней) комнате плана: иначе
	// соседние комнаты чуть разного размера подписаны по-разному. Мелкая комната, где
	// и этого не влезет, упрощает свою подпись сама
	const labels = rooms.map((room, index) => {
		const ceiling = layers.ceiling.visible && layers.ceiling.items[room.id];
		const ceilingHeight = ceiling
			? roomFinishedCeilingHeight(layers.walls, room, layers.ceiling.items)
			: undefined;
		const text = roomLabelText(room.area);
		const name = roomDisplayName(layers.rooms, room.id, index);
		const ceilingMark =
			ceilingHeight === undefined ? undefined : roomLabelCeilingHeight(ceilingHeight);
		const own = roomLabelDetail(room, { area: text, name, ceiling: ceilingMark }, mmPerPx);
		return { room, text, name, ceilingMark, own };
	});
	const typical = labels.map(({ own }) => DETAIL_RANK[own]).toSorted((a, b) => a - b)[
		Math.floor(labels.length / 2)
	];

	return (
		<g>
			{labels.map(({ room, text, name, ceilingMark, own }) => {
				const rank = Math.min(DETAIL_RANK[own], typical ?? DETAIL_RANK.full);
				return (
					<RoomLabel
						key={`label-${room.id}`}
						text={text}
						name={name}
						ceilingHeight={ceilingMark}
						at={room.label}
						mmPerPx={mmPerPx}
						detail={DETAILS[rank]}
						onPointerDown={onLabelPointerDown ? (e) => onLabelPointerDown(e, room) : undefined}
					/>
				);
			})}
		</g>
	);
};
